/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState, useRef, useMemo } from 'react'
import Box from '@mui/material/Box'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { FormProvider, useForm, type FieldValues, type SubmitHandler } from 'react-hook-form'
import * as Sentry from '@sentry/react'
import { getAcademicUnitDepth, getSlotLoading } from '../../../../store/slices/distributionPlanSlice/selectors'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import { type InstructorAddedQuestions, SlotStatus, type ISlot, type ISurveyTemplate, type AcademicLevelAddedQuestions, type SectionAddedQuestions } from '../../../../models/DistributionPlan'
import { useTheme } from '@mui/material/styles'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import ValidationAlert from '../../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors, type ServerFieldErrorSetter } from '../../../../utils/showServerFieldErrors'
import { useNavigate } from 'react-router-dom'
import { getDirtyValues } from '../../../../utils/onlyUpdatedFields'
import { HttpStatusCode } from '../../../../consts/httpStatusCode'
import { ApiAutoComplete } from '../../../../components/FormElements/ApiAutoComplete'
import ROUTES from '../../../../consts/paths'
import FormSwitch from '../../../../components/FormElements/FormSwitch'
import FormInput from '../../../../components/FormElements/FormInput'
import { Collapse, Divider, Typography } from '@mui/material'
import FormCheckbox from '../../../../components/FormElements/FormCheckBox'
import AnimatedContainer from '../../../../components/Animations/AnimatedContainer'
import { grey } from '../../../../colors'
import MultiSelectApiAutoComplete from '../../../../components/FormElements/MultiSelectApiAutoComplete'
import { distributionFormSchema, type DistributionFormContext } from '../schema'
import { type IAcademicUnit } from '../../../../models/AcademicUnit'
import { ReleaseMode } from '../../../../helpers/parseReleaseTimestamp'
import { SurveyProjectTypeAndRollingBasisToggle } from './SurveyProjectTypeAndRollingBasisToggle'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AutoImportManager, DistributionManager } from '../../../../APIManager'
import { defaultAddedQuestionsValues } from './constants'
import { DefaultSurveySelection } from './DefaultSurveySelection'
import { LastUpdateMeta } from '../../../../components/GenericModal/ChangeMetaInfo'
import { notification } from '../../../../components/Notifications'
import { notifyError } from '../../../../utils/notifyError'
import { useSlotTaskContext } from '../../../../contexts/SlotTaskContext'
import GenericModal from '../../../../components/GenericModal'
import {
  ApplyDefaultQuestionsConflictModal,
  type ApplyDefaultQuestionsConflict,
  type ApplyDefaultQuestionsConflictSummary
} from './ApplyDefaultQuestionsConflictModal'
import { CapLoweringConflictModal, type CapLoweringConflict } from './CapLoweringConflictModal'
import { SectionCapLoweringConflictModal, type SectionCapLoweringConflict } from './SectionCapLoweringConflictModal'
import {
  ApplySectionDefaultQuestionsConflictModal,
  type ApplySectionDefaultQuestionsConflict,
  type ApplySectionDefaultQuestionsConflictSummary
} from './ApplySectionDefaultQuestionsConflictModal'

interface FormType {
  templateId: string
  termId: string
  startTimestamp: Date | null
  endTimestamp: Date | null
  releaseTimestamp: Date | null
  isRollingResultsEnabled: boolean
  isInstructorAddedQuestionsAllowed?: boolean | null
  // Top-level on Slot per Backend (slot_schema.py:108) — copied here (not nested inside
  // instructorAddedQuestions) only for Copy Previous Project Settings' create-flow carry-over
  // gate in onSubmit; not otherwise submitted (Backend derives it on create, dump_only on PATCH).
  isDefaultInstructorAddedQuestionsEnabled?: boolean
  instructorAddedQuestions?: InstructorAddedQuestions | null
  isAcademicLevelAddedQuestionsAllowed?: boolean | null
  academicLevelAddedQuestions?: AcademicLevelAddedQuestions | null
  // CR-295 — one shared set per course section, as opposed to instructorAddedQuestions which
  // repeats per instructor.
  isSectionAddedQuestionsAllowed?: boolean | null
  sectionAddedQuestions?: SectionAddedQuestions | null
  isSlotAnonymous: boolean
  isAcademicUnitOptOutEnabled?: boolean
  academicUnitOptOutCategoryIds?: string[]
  academicUnitOptOutDeadline?: number
  isInstructorOptOutEnabled?: boolean
  instructorOptOutDeadline?: number
  isInstructorOptInOverrideEnabled?: boolean
}

interface DefaultInstructorTemplateOption {
  templateId: string
  title: string
  questionCount?: number
}

type ModalStep = 'SELECTDEFAULT' | 'TOGGLE' | 'FORM'
const STEP: Record<ModalStep, ModalStep> = { SELECTDEFAULT: 'SELECTDEFAULT', TOGGLE: 'TOGGLE', FORM: 'FORM' }

/**
 * The only two values the cap-lowering 409's `questionType` can carry — `SlotQuestionTypes` names on
 * the wire, a different vocabulary from `ADDED_QUESTION_TYPES`, which holds this app's own
 * search-param labels. `ACADEMIC_LEVEL_ADDED` never reaches that gate: the backend's
 * `check_instructor_added_questions_cap_conflict` returns early for it.
 */
const CAP_CONFLICT_QUESTION_TYPES = {
  instructorAdded: 'INSTRUCTOR_ADDED',
  sectionAdded: 'SECTION_ADDED'
} as const

interface CreateDistributionProps {
  selectedRow?: ISlot & Pick<ISurveyTemplate, 'templateRevision'> | Record<string, any>
  projectId?: string
  shouldLoadLatestDefaults?: boolean
  handleCloseModal?: () => void
  handleAddSlot?: (slotId: string) => void
  onSaved?: () => void
  /**
   * Called when a background task started by the save finishes, rather than when the save returns.
   * Kept separate from `onSaved` because the two need different data: `onSaved` fires immediately
   * and may capture click-time state, while this one runs minutes later and must read whatever the
   * user is looking at then. Falls back to `onSaved` when not supplied.
   */
  onTaskComplete?: () => void
}
export const CreateAndEditDistributionPlan: React.FC<CreateDistributionProps> = ({
  selectedRow,
  projectId = '',
  shouldLoadLatestDefaults = true,
  handleCloseModal = () => { },
  handleAddSlot,
  onSaved,
  onTaskComplete
}) => {
  const [errors, setErrors] = useState<unknown>({})
  const theme = useTheme()
  const navigate = useNavigate()
  const isEditAction = Boolean(selectedRow)
  const loading = getSlotLoading()
  const [isCreating, setIsCreating] = useState(false)
  const [step, setStep] = useState<ModalStep>(STEP.SELECTDEFAULT)

  const queryClient = useQueryClient()
  const { setTask, checkIfBlocked } = useSlotTaskContext()
  const {
    slotNodeDetails,
    instructorAddedQuestions,
    isDefaultInstructorAddedQuestionsEnabled: isDefaultInstructorAddedQuestionsEnabledOnSlot,
    isInstructorAddedQuestionsAllowed,
    isAcademicLevelAddedQuestionsAllowed,
    academicLevelAddedQuestions,
    // CR-295. `is_section_added_questions_allowed` is a real Slot column as of the backend's
    // three-step change: the type is switched on by PATCHing it, its limit by PATCHing
    // `section_added_questions`, and a default template is an optional third step. Before that,
    // enablement *was* the settings row and this flag did not exist — hence the fallback below,
    // which keeps already-enabled projects reading correctly either side of their migration.
    isSectionAddedQuestionsAllowed,
    sectionAddedQuestions
  } = (selectedRow as any) ?? {}

  const isDisabled = Boolean(selectedRow && slotNodeDetails?.slotStatus !== SlotStatus.Draft)
  const isSlotStatusLive = isDisabled

  // Default (template-sourced) instructor added questions — enable, change, and disable are all
  // local-only until the modal Save. Neither checking/unchecking the box nor picking a template
  // has a button (or immediate API call) of its own — every combination rides along with the
  // main Save action in onSubmit, gated by hasPendingDefaultTemplateChange below so Save doesn't
  // needlessly resubmit an already-applied template on every unrelated edit.
  const [isDefaultInstructorQuestionsEnabled, setIsDefaultInstructorQuestionsEnabled] = useState(
    Boolean(isDefaultInstructorAddedQuestionsEnabledOnSlot)
  )
  const [selectedDefaultTemplate, setSelectedDefaultTemplate] = useState<DefaultInstructorTemplateOption | null>(
    instructorAddedQuestions?.defaultTemplateId
      ? {
          templateId: instructorAddedQuestions.defaultTemplateId,
          title: instructorAddedQuestions.defaultTemplateTitle ?? ''
        }
      : null
  )
  // Snapshot of what's actually applied on the slot today, captured once at mount — comparing
  // live state against props directly would misfire since props never change after mount.
  const initialDefaultEnabledRef = useRef(Boolean(isDefaultInstructorAddedQuestionsEnabledOnSlot))
  const initialDefaultTemplateIdRef = useRef(instructorAddedQuestions?.defaultTemplateId ?? null)
  // Enabled: pending if newly enabled or the picked template differs from what's applied today.
  // Unchecked: pending only if the slot is currently enabled server-side — nothing to disable
  // otherwise. Either direction needs Save to actually take effect.
  const hasPendingDefaultTemplateChange = Boolean(
    isDefaultInstructorQuestionsEnabled
      ? selectedDefaultTemplate &&
        (!initialDefaultEnabledRef.current || selectedDefaultTemplate.templateId !== initialDefaultTemplateIdRef.current)
      : initialDefaultEnabledRef.current
  )
  const [defaultQuestionsConflictData, setDefaultQuestionsConflictData] = useState<{
    conflicts: ApplyDefaultQuestionsConflict[]
    summary: ApplyDefaultQuestionsConflictSummary
  } | null>(null)
  // Separate from defaultQuestionsConflictData above — this is the main slot PATCH's own 409
  // (lowering "Number of questions per Instructor" below an instructor-touched section's current
  // count), not apply_default's. pendingData is the exact dirty-fields payload already sent, so
  // confirming can resend it unchanged plus `instructorAddedQuestions.confirm: true`.
  // The section-added counterpart. Separate state rather than a discriminated union so that neither
  // dialog can ever be handed the other's payload.
  const [sectionCapLoweringConflict, setSectionCapLoweringConflict] = useState<{
    conflicts: SectionCapLoweringConflict[]
    newQuestionCount?: number
    pendingData: Record<string, any>
  } | null>(null)
  const [capLoweringConflict, setCapLoweringConflict] = useState<{
    conflicts: CapLoweringConflict[]
    newQuestionCount: number
    pendingData: Record<string, any>
  } | null>(null)
  // Shared busy flag for apply + disable so the checkbox/picker cannot be raced while either
  // request is outstanding (e.g. uncheck → quick re-check → pick a new template before disable fails).
  const [isDefaultQuestionsBusy, setIsDefaultQuestionsBusy] = useState(false)
  // Isolated Controller wiring for the checkbox + template picker only — passing this explicit
  // `control` (instead of relying on the ambient FormProvider) keeps this section's state out of
  // the main form's dirtyFields/isDirty, so it can never confusingly enable the main Save button.
  const { control: defaultTemplateControl, setError: setDefaultTemplateError, clearErrors: clearDefaultTemplateErrors } = useForm<FieldValues>({
    defaultValues: {
      templateId: instructorAddedQuestions?.defaultTemplateId ?? '',
      isDefaultEnabled: Boolean(isDefaultInstructorAddedQuestionsEnabledOnSlot)
    }
  })

  // CR-295 — the section-added type's own default template. Deliberately a parallel set of
  // state and its own isolated `control`, not shared with the instructor picker above: the two
  // settings rows are independent by design (one per question type, per
  // `uq_slot_allowable_question_settings`), so neither may read or write the other's template.
  // Sharing any of this state is how they would start to.
  // Derived from the attached template rather than a flag of its own: `apply_default` is the only
  // thing that attaches one, and a settings row with no template is indistinguishable from the
  // default being off — the backend's own words.
  const isSectionDefaultAppliedOnSlot = sectionAddedQuestions?.defaultTemplateId != null
  // Whether the type is on for this project. `??`, not `||`: the flag is authoritative when present,
  // including when it is explicitly `false`. With `||`, a project whose type had just been turned
  // off would read as on again on reopen — the settings row survives a turn-off, since it holds the
  // limit — and the admin would face the destructive confirmation a second time. The fallback is
  // only for a project enabled under the old one-call flow, before the flag existed.
  const isSectionTypeEnabledOnSlot = isSectionAddedQuestionsAllowed ?? (sectionAddedQuestions != null)
  const [isDefaultSectionQuestionsEnabled, setIsDefaultSectionQuestionsEnabled] = useState(
    isSectionDefaultAppliedOnSlot
  )
  const [selectedSectionDefaultTemplate, setSelectedSectionDefaultTemplate] =
    useState<DefaultInstructorTemplateOption | null>(
      sectionAddedQuestions?.defaultTemplateId
        ? {
            templateId: sectionAddedQuestions.defaultTemplateId,
            title: sectionAddedQuestions.defaultTemplateTitle ?? ''
          }
        : null
    )
  const initialSectionDefaultEnabledRef = useRef(isSectionDefaultAppliedOnSlot)
  const initialSectionDefaultTemplateIdRef = useRef(sectionAddedQuestions?.defaultTemplateId ?? null)
  const hasPendingSectionDefaultChange = Boolean(
    isDefaultSectionQuestionsEnabled
      ? selectedSectionDefaultTemplate &&
        (!initialSectionDefaultEnabledRef.current ||
          selectedSectionDefaultTemplate.templateId !== initialSectionDefaultTemplateIdRef.current)
      : initialSectionDefaultEnabledRef.current
  )
  const [sectionConflictData, setSectionConflictData] = useState<{
    conflicts: ApplySectionDefaultQuestionsConflict[]
    summary: ApplySectionDefaultQuestionsConflictSummary
  } | null>(null)
  const { control: sectionDefaultTemplateControl, setError: setSectionDefaultTemplateError, clearErrors: clearSectionDefaultTemplateErrors } = useForm<FieldValues>({
    defaultValues: {
      sectionTemplateId: sectionAddedQuestions?.defaultTemplateId ?? '',
      isSectionDefaultEnabled: isSectionDefaultAppliedOnSlot
    }
  })

  // 'skipped' = we refused to start (guard failed, or another task is already running on the
  // slot) and the caller must treat that as "not applied".
  type ApplyDefaultOutcome = 'applied' | 'conflict' | 'error' | 'skipped'

  // `skipTaskTracking` guards against clobbering the main Save PATCH's own tracked task:
  // useSlotBackgroundTask stores exactly one taskId per slot, so a second setTask call in the
  // same submit would silently drop the first task's completion toast. Unreachable today (this
  // feature is Draft-only; the main PATCH only returns a task while Published/Live), but kept
  // as a safety net in case that gating ever changes — falls back to an immediate toast instead
  // of losing tracking of whichever task registers second.
  const applyDefaultInstructorQuestions = async (
    confirm = false,
    { skipTaskTracking = false, force = false }: { skipTaskTracking?: boolean, force?: boolean } = {}
  ): Promise<ApplyDefaultOutcome> => {
    if (!selectedRow?.slotId || !selectedDefaultTemplate || isDefaultQuestionsBusy) return 'skipped'
    if (await checkIfBlocked()) return 'skipped'
    setIsDefaultQuestionsBusy(true)
    try {
      const { data, status } = await AutoImportManager.applyDefaultInstructorAddedQuestions(selectedRow.slotId, {
        enabled: true,
        defaultSurveyTemplateId: selectedDefaultTemplate.templateId,
        confirm,
        force
      })
      if (status === HttpStatusCode.ACCEPTED) {
        const runningMessage = data.message ?? 'Applying default questions...'
        if (!skipTaskTracking) {
          setTask(data.taskId, runningMessage, 'Default instructor questions applied.')
        }
        notification(runningMessage, { variant: 'info' })
        setDefaultQuestionsConflictData(null)
        await queryClient.invalidateQueries({ queryKey: ['slotDataById', selectedRow.slotId] })
      }
      // Move the baseline forward to what's now actually applied — otherwise a later
      // disable-then-re-enable-the-same-template sequence would read as "no change" against
      // the stale mount-time snapshot and Save would silently skip re-applying it.
      initialDefaultEnabledRef.current = true
      initialDefaultTemplateIdRef.current = selectedDefaultTemplate.templateId
      return 'applied'
    } catch (error: any) {
      // src/utils/api.ts's interceptor rejects with a normalized structured error —
      // status/data are top-level, matching the repo-wide apiError.status === 404 convention.
      // Guard on both conflicts and summary — a malformed 409 falls through to the
      // generic error toast instead of silently resetting the button with no feedback.
      if (error.status === 409 && error.data?.conflicts?.length && error.data?.summary) {
        setDefaultQuestionsConflictData({ conflicts: error.data.conflicts, summary: error.data.summary })
        return 'conflict'
      }
      notifyError(error)
      return 'error'
    } finally {
      setIsDefaultQuestionsBusy(false)
    }
  }

  const disableDefaultInstructorQuestions = async (
    { skipTaskTracking = false }: { skipTaskTracking?: boolean } = {}
  ): Promise<ApplyDefaultOutcome> => {
    if (!selectedRow?.slotId || isDefaultQuestionsBusy) return 'skipped'
    // Match other slot-level background-task triggers: refuse to start disable while an
    // unrelated task is already running on this slot.
    if (await checkIfBlocked()) return 'skipped'
    setIsDefaultQuestionsBusy(true)
    try {
      const { data, status } = await AutoImportManager.applyDefaultInstructorAddedQuestions(selectedRow.slotId, { enabled: false })
      if (status === HttpStatusCode.ACCEPTED) {
        const runningMessage = data.message ?? 'Removing unedited default questions...'
        if (!skipTaskTracking) {
          setTask(data.taskId, runningMessage, 'Default disabled — untouched seeded questions were removed.')
        }
        notification(runningMessage, { variant: 'info' })
        await queryClient.invalidateQueries({ queryKey: ['slotDataById', selectedRow.slotId] })
      }
      initialDefaultEnabledRef.current = false
      initialDefaultTemplateIdRef.current = null
      return 'applied'
    } catch (error) {
      // Roll back the unchecked box — the Backend call failed, so the default is still
      // enabled there. selectedDefaultTemplate was never cleared (uncheck is local-only
      // until Save), so there's nothing else to restore.
      notifyError(error)
      setIsDefaultInstructorQuestionsEnabled(true)
      return 'error'
    } finally {
      setIsDefaultQuestionsBusy(false)
    }
  }

  const handleToggleDefaultInstructorQuestions = (checked: boolean): void => {
    if (isDefaultQuestionsBusy) return
    // Local only, either direction — nothing is sent to the Backend until Save.
    setIsDefaultInstructorQuestionsEnabled(checked)
    setDefaultQuestionsConflictData(null)
  }

  // CR-295 — section-added equivalents. Same shape as the instructor pair above: local-only
  // until Save, then fired from onSubmit. Kept as separate functions rather than parameterising
  // the instructor ones, because the 409 bodies differ (sections vs instructor-section pairs)
  // and so does the modal that renders them.
  //
  const applyDefaultSectionQuestions = async (
    confirm = false,
    { skipTaskTracking = false, force = false }: { skipTaskTracking?: boolean, force?: boolean } = {}
  ): Promise<ApplyDefaultOutcome> => {
    if (!selectedRow?.slotId || !selectedSectionDefaultTemplate || isDefaultQuestionsBusy) return 'skipped'
    // Same rule as the instructor path: never start a second slot-level background task while
    // one is already running — useSlotBackgroundTask tracks exactly one taskId per slot.
    if (await checkIfBlocked()) return 'skipped'
    setIsDefaultQuestionsBusy(true)
    try {
      const { data, status } = await AutoImportManager.applyDefaultSectionAddedQuestions(selectedRow.slotId, {
        enabled: true,
        defaultSurveyTemplateId: selectedSectionDefaultTemplate.templateId,
        ...(confirm ? { confirm: true } : {}),
        ...(force ? { force: true } : {})
      })
      if (status === HttpStatusCode.ACCEPTED) {
        const runningMessage = data?.message ?? 'Applying default Course Section questions...'
        if (!skipTaskTracking) {
          setTask(data.taskId, runningMessage, 'Default Course Section questions applied.')
        }
        notification(runningMessage, { variant: 'info' })
        // The main PATCH's own invalidation already ran before this call, so without this the
        // slot cache keeps the pre-apply default state and reopening the modal shows the
        // checkbox unticked.
        await queryClient.invalidateQueries({ queryKey: ['slotDataById', selectedRow.slotId] })
      }
      setSectionConflictData(null)
      // Move the baseline forward to what is now applied — otherwise a later
      // disable-then-re-enable of the same template reads as "no change" against the stale
      // mount-time snapshot and Save silently skips re-applying it.
      initialSectionDefaultEnabledRef.current = true
      initialSectionDefaultTemplateIdRef.current = selectedSectionDefaultTemplate.templateId
      return 'applied'
    } catch (error: any) {
      // Guarded on both conflicts and summary, matching the instructor path: a malformed 409
      // must fall through to the error branch rather than open a modal with nothing in it.
      if (error?.status === 409 && error.data?.conflicts?.length && error.data?.summary) {
        setSectionConflictData({ conflicts: error.data.conflicts, summary: error.data.summary })
        return 'conflict'
      }
      // Must surface: the PATCH has already committed, and this outcome keeps the modal open —
      // without a toast the admin sees a Save that neither closed nor complained.
      notifyError(error)
      return 'error'
    } finally {
      setIsDefaultQuestionsBusy(false)
    }
  }

  const disableDefaultSectionQuestions = async (
    { skipTaskTracking = false }: { skipTaskTracking?: boolean } = {}
  ): Promise<ApplyDefaultOutcome> => {
    if (!selectedRow?.slotId || isDefaultQuestionsBusy) return 'skipped'
    if (await checkIfBlocked()) return 'skipped'
    setIsDefaultQuestionsBusy(true)
    try {
      // Disable is safe by construction on the Backend — it only removes seeded content nobody
      // touched — so there is no confirmation and no conflict path here.
      const { data, status } = await AutoImportManager.applyDefaultSectionAddedQuestions(
        selectedRow.slotId,
        { enabled: false }
      )
      if (status === HttpStatusCode.ACCEPTED) {
        const runningMessage = data?.message ?? 'Removing unedited default Course Section questions...'
        if (!skipTaskTracking) {
          setTask(data.taskId, runningMessage, 'Default disabled — untouched seeded Course Section questions were removed.')
        }
        notification(runningMessage, { variant: 'info' })
        await queryClient.invalidateQueries({ queryKey: ['slotDataById', selectedRow.slotId] })
      }
      initialSectionDefaultEnabledRef.current = false
      initialSectionDefaultTemplateIdRef.current = null
      return 'applied'
    } catch (error: any) {
      notifyError(error)
      // Roll the checkbox back so the UI does not claim a disable that did not happen.
      setIsDefaultSectionQuestionsEnabled(true)
      return 'error'
    } finally {
      setIsDefaultQuestionsBusy(false)
    }
  }

  /**
   * Switching the type off removes it from the whole project, and the backend clears every section's
   * untouched seeded questions behind it. Instructor-authored content survives, but nothing in the
   * modal shows how many sections are affected — so Save confirms first rather than doing it on the
   * strength of one unticked box. Approved by Nauman, 2026-08-21.
   *
   * Only when it was on to begin with: unticking a box the admin ticked a moment ago in this same
   * modal has nothing to undo.
   */
  const [isConfirmingSectionTypeOff, setIsConfirmingSectionTypeOff] = useState(false)
  const hasConfirmedSectionTypeOff = useRef(false)

  /**
   * Which question types the admin has actually been shown conflicts for, and acknowledged. Both
   * types can return the cap-lowering 409, but the body carries one type's conflicts at a time — so
   * lowering both caps in one Save means two rounds. This accumulates rather than replacing:
   * stamping only the latest type would drop the earlier acknowledgement and 409 on it again, and
   * stamping both up front would acknowledge conflicts that were never rendered.
   */
  const confirmedCapTypes = useRef<Set<'instructor' | 'section'>>(new Set())

  const handleToggleDefaultSectionQuestions = (checked: boolean): void => {
    if (isDefaultQuestionsBusy) return
    setIsDefaultSectionQuestionsEnabled(checked)
    setSectionConflictData(null)
  }
  const academicUnitList = getAcademicUnitDepth() || []
  const academicUnitDepth = academicUnitList[0]?.depth ?? 0
  // Field enabling/disabling based on status
  // Draft: All fields enabled
  // Published: Term, Survey Template → disabled
  // Live: Term, Survey Template, Start Time → disabled
  // Closed: Only Release Time enabled
  const isSlotInDraft = slotNodeDetails?.slotStatus === SlotStatus.Draft
  const isSlotInPublished = slotNodeDetails?.slotStatus === SlotStatus.Published
  const isSlotInLive = slotNodeDetails?.slotStatus === SlotStatus.Live
  const isSlotInClosed = slotNodeDetails?.slotStatus === SlotStatus.Closed

  const canEditTerm = !isEditAction || isSlotInDraft
  const canEditSurveyTemplate = !isEditAction || isSlotInDraft
  const canEditStartTimestamp = !isEditAction || isSlotInDraft || isSlotInPublished
  const canEditEndTimestamp = !isEditAction || isSlotInDraft || isSlotInPublished || isSlotInLive
  const canEditReleaseTimestamp =
    !isEditAction || isSlotInDraft || isSlotInPublished || isSlotInLive || isSlotInClosed
  const canEditAdvancedSettings = !isEditAction || isSlotInDraft

  const lastChangedField = useRef<'start' | 'end' | 'release' | null>(null)
  // Captured on mount — safe because this modal remounts fresh on every open.
  const todayMin = useMemo(() => new Date(), [])

  // lastChangedField is intentionally excluded from deps — it is a ref whose .current
  // is read at invocation time, so the memoized function always sees the latest value.
  const resolver = useMemo(() => {
    const base = yupResolver(distributionFormSchema)
    return async (values: Parameters<typeof base>[0], _context: Parameters<typeof base>[1], options: Parameters<typeof base>[2]) =>
      await base(
        values,
        {
          canEditStartTimestamp,
          canEditEndTimestamp,
          canEditReleaseTimestamp,
          slotStatus: slotNodeDetails?.slotStatus,
          lastChangedField: lastChangedField.current
        } satisfies DistributionFormContext,
        options
      )
  }, [canEditStartTimestamp, canEditEndTimestamp, canEditReleaseTimestamp, slotNodeDetails?.slotStatus])

  const methods = useForm<any>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      templateId: selectedRow?.surveyTemplate?.templateId ?? '',
      surveyTemplate: selectedRow?.surveyTemplate ?? null,
      termId: selectedRow?.term?.termId ?? '',
      // Do not set default dates automatically - use null/undefined instead
      startTimestamp: selectedRow?.slotStartTimestamp ?? undefined,
      endTimestamp: selectedRow?.slotEndTimestamp ?? undefined,
      releaseTimestamp: selectedRow?.releaseTimestamp ?? undefined,
      isRollingResultsEnabled: selectedRow?.isRollingResultsEnabled ?? false,
      isSlotAnonymous: selectedRow?.isSlotAnonymous ?? false,
      isInstructorAddedQuestionsAllowed: isInstructorAddedQuestionsAllowed ?? false,
      isDefaultInstructorAddedQuestionsEnabled: Boolean(isDefaultInstructorAddedQuestionsEnabledOnSlot),
      instructorAddedQuestions: instructorAddedQuestions ?? defaultAddedQuestionsValues.instructorAddedQuestions,
      isAcademicLevelAddedQuestionsAllowed: isAcademicLevelAddedQuestionsAllowed ?? false,
      academicLevelAddedQuestions: academicLevelAddedQuestions ?? defaultAddedQuestionsValues.academicLevelAddedQuestions,
      isSectionAddedQuestionsAllowed: isSectionTypeEnabledOnSlot,
      sectionAddedQuestions: sectionAddedQuestions ?? defaultAddedQuestionsValues.sectionAddedQuestions,
      isAcademicUnitOptOutEnabled: selectedRow?.isAcademicUnitOptOutEnabled ?? false,
      academicUnitOptOutCategoryIds: selectedRow?.academicUnitOptOutCategoryIds,
      academicUnitOptOutDeadline: selectedRow?.academicUnitOptOutDeadline ?? undefined,
      isInstructorOptOutEnabled: selectedRow?.isInstructorOptOutEnabled ?? false,
      instructorOptOutDeadline: selectedRow?.instructorOptOutDeadline ?? undefined,
      isInstructorOptInOverrideEnabled: selectedRow?.isInstructorOptInOverrideEnabled ?? true

    }
  })

  const { handleSubmit, control, watch, trigger, formState: { isDirty, dirtyFields } } = methods as any

  const editSlotMutation = useMutation({
    meta: { silent: true },
    mutationFn: async ({ slotId, data }: { slotId: string, data: any }) =>
      await DistributionManager.updateDistributionPlan(slotId, data),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['slotDataById', variables.slotId] })
    }
  })

  const createSlotMutation = useMutation({
    meta: { silent: true },
    mutationFn: async ({ projectId, data }: { projectId: string, data: any }) =>
      await DistributionManager.createDistributionPlan(projectId, data)
  })

  const reportSaveError = (err: unknown): void => {
    const highlightField: ServerFieldErrorSetter = (name, fieldError, options) => {
      bindSetError(methods.setError)(name, fieldError, options)
      if (name === 'sectionAddedQuestions.defaultTemplateId') {
        setSectionDefaultTemplateError('sectionTemplateId', fieldError, { shouldFocus: false })
        return
      }
      if (name === 'instructorAddedQuestions.defaultTemplateId' || name === 'defaultTemplateId') {
        setDefaultTemplateError('templateId', fieldError, { shouldFocus: false })
      }
    }
    setErrors(showServerFieldErrors(err, highlightField, () => {
      methods.clearErrors()
      clearDefaultTemplateErrors()
      clearSectionDefaultTemplateErrors()
    }))
  }

  // Shared by onSubmit's first attempt and confirmCapLoweringConflict's resubmit — the rest of the
  // form has already saved by the time either calls this, so a non-'applied' default-template
  // outcome (conflict, blocked, or a genuine error) only pauses closing the modal, same as before.
  const afterEditSlotPatchSuccess = async (response: any): Promise<boolean> => {
    const mainTaskId: string | undefined = response?.status === HttpStatusCode.ACCEPTED ? response.data?.taskId : undefined
    if (mainTaskId) {
      // `onSaved` refetches the Course Sections table, but the callers invoke it as soon as the save
      // returns — which is too early for a 202: the work that changes section rows has not run yet.
      // Release Date Sync is the case that made this visible. Moving a Closed project's release date
      // earlier now clamps every section Admin/Instructor Release Date that sat after it back to the
      // new date, in a Celery task, so the cells the admin is looking at are stale the moment they
      // save. Refetching again on completion is what `slotStatusUpdateHandler` already does for the
      // status transitions, via this same fourth argument.
      //
      // Attached to whichever task the response carried, not just the sync: the backend enqueues the
      // clamp outside the endpoint's mutually-exclusive task chain, so on a Closed project an
      // auto-import task can claim the response while the clamp runs alongside it. Refreshing after
      // any 202 covers that case too, and is harmless for tasks that touch no section dates.
      // `onTaskComplete` rather than `onSaved`: the save-time callback closes over the section
      // table's params from the click-time render, and the admin can change view, page or filters
      // while the task runs. Refetching with those stale params on completion would overwrite the
      // table they are now looking at. The task-completion callback reads the live URL instead —
      // the same reasoning as `refreshSections` in the Course Sections feature.
      setTask(mainTaskId, response.data?.message ?? '', 'Survey project updated successfully.', () => { (onTaskComplete ?? onSaved)?.() })
    } else {
      const termName = response?.data?.term?.termName ?? selectedRow?.term?.termName ?? ''
      notification('Term updated successfully', { variant: 'success', subTitle: termName ? `Term: ${termName}` : undefined })
    }
    // Gated on the switch's current position rather than on a turn-off transition, and that covers
    // detaching as well as attaching. While the type is off we touch its default in neither
    // direction: the checkbox lives inside the block that collapses with the switch, so the only way
    // to reach it is with the type on — meaning a cleared picker while the switch is off never
    // represents an admin's decision to detach. It can instead be our own clear-on-off effect below
    // firing after a brief on → off, and for a type that is off on the slot *but still has a template
    // attached* — the state the backend keeps exitable — that read as "the admin turned the default
    // off" and detached a template nobody touched, taking its seeded questions with it.
    // `isTurning*TypeOff` does not catch that: it compares against the slot, which was never on.
    if (hasPendingDefaultTemplateChange && isInstructorAddedQuestions) {
      const outcome = isDefaultInstructorQuestionsEnabled
        ? await applyDefaultInstructorQuestions(false, { skipTaskTracking: Boolean(mainTaskId) })
        : await disableDefaultInstructorQuestions({ skipTaskTracking: Boolean(mainTaskId) })
      if (outcome !== 'applied') return false
    }
    // CR-295 — the section-added default rides along with the same Save, after the instructor
    // one. Sequential rather than parallel on purpose: both hit apply_default on the same slot,
    // and they share `isDefaultQuestionsBusy`, so firing them together would have the second
    // return 'skipped' and silently not apply.
    // Skipped entirely whenever the switch is off, in both directions — see the note on the
    // instructor block above, which this mirrors. `apply_default` refuses on a disabled type
    // ("Please turn on Section Added Questions before enabling the default."), and the natural way
    // to switch off is to untick the default checkbox and then the switch — which would otherwise
    // fire a disable call against a type the PATCH disabled milliseconds earlier, re-tick the
    // checkbox on rollback, and hold the modal open complaining about a save that committed.
    // Turning the type off already removes the default with it.
    if (hasPendingSectionDefaultChange && isSectionAddedQuestions) {
      // Only one taskId is tracked per slot, so hand off to a plain toast if either the main
      // PATCH or the instructor apply above has already claimed it.
      const skipTaskTracking = Boolean(mainTaskId) || hasPendingDefaultTemplateChange
      const outcome = isDefaultSectionQuestionsEnabled
        ? await applyDefaultSectionQuestions(false, { skipTaskTracking })
        : await disableDefaultSectionQuestions({ skipTaskTracking })
      if (outcome !== 'applied') return false
    }
    return true
  }

  /**
   * Routes the backend's cap-lowering 409 to the dialog for the type that actually conflicted, and
   * reports whether it handled the error at all.
   *
   * Which type it is comes from the body's `questionType` — `INSTRUCTOR_ADDED` or `SECTION_ADDED`,
   * the only two values that reach this gate. Rendering the wrong one produced "N instructors over
   * the new limit" with a blank Instructor column.
   *
   * Shared by the first attempt and the resubmit. The resubmit needs it too: with two types able to
   * conflict, acknowledging one can surface a 409 for the other, and dropping that into the generic
   * error banner would strand the admin on a conflict list they were never shown.
   */
  const routeCapLoweringConflict = (err: any, pendingData: Record<string, any>): boolean => {
    if (err?.status !== 409 || !Array.isArray(err?.data?.conflicts) || err.data.conflicts.length === 0) {
      return false
    }
    // Routing a conflict leaves the modal open, which is the same situation as the `!shouldClose`
    // and outer-catch paths: the admin can still change their mind about the turn-off, so the
    // acknowledgement has to be asked for again rather than latching. This return is *earlier* than
    // both of those, so clearing it there was not enough — a 409 on a Save that also turned the type
    // off left the confirmation banked, and the next Save in the same session removed every
    // section's shared questions without asking.
    hasConfirmedSectionTypeOff.current = false
    const conflicts: Array<Record<string, any>> = err.data.conflicts
    // `questionType` is the discriminator, on the body since CR-295 and test-pinned in Backend
    // #2136 — before that it was present but unasserted, so removing it passed every backend test.
    // Only `INSTRUCTOR_ADDED` and `SECTION_ADDED` ever arrive here: the cap-conflict check returns
    // early for `ACADEMIC_LEVEL_ADDED`, so there is deliberately no branch for it.
    //
    // The entry-shape fallback stays until the field is confirmed everywhere this frontend is
    // deployed, since the frontend and backend promote independently. It reads the same invariant it
    // always did — an instructor-added entry always carries a real `instructor_user_id`, a
    // section-added entry omits the key, because a section-owned set has no single instructor to
    // name — and can be deleted once every environment is past CR-295's backend.
    const questionType: string | undefined = err.data.questionType
    const isSectionShaped = questionType != null
      ? questionType === CAP_CONFLICT_QUESTION_TYPES.sectionAdded
      : conflicts.every((conflict) => conflict?.instructorUserId == null)
    if (isSectionShaped) {
      setSectionCapLoweringConflict({
        conflicts: conflicts as SectionCapLoweringConflict[],
        newQuestionCount: err.data.newQuestionCount ?? pendingData?.sectionAddedQuestions?.questionCount,
        pendingData
      })
      return true
    }
    setCapLoweringConflict({
      conflicts: err.data.conflicts,
      newQuestionCount: err.data.newQuestionCount,
      pendingData
    })
    return true
  }

  // Resubmits the exact PATCH payload the admin already tried, adding `confirm: true` — per the
  // Backend's cap-lowering conflict contract, this is pure acknowledgement (it never trims or
  // changes the touched section's content).
  //
  // The flag goes *inside* the block that conflicted, and only into blocks whose conflicts the admin
  // has actually been shown — `confirmedType` says which dialog this call came from. A top-level
  // `confirm` is silently ignored, and stamping a block the admin never saw a list for acknowledges
  // a cap lowering on someone else's behalf.
  const confirmCapLoweringConflict = async (
    pendingData: Record<string, any>,
    confirmedType: 'instructor' | 'section'
  ): Promise<void> => {
    if (!selectedRow?.slotId) return
    confirmedCapTypes.current.add(confirmedType)
    setIsCreating(true)
    // Down before the resubmit, not after it: if this attempt 409s for the *other* type, that
    // dialog has to be the only one on screen.
    setCapLoweringConflict(null)
    setSectionCapLoweringConflict(null)
    try {
      const pending = pendingData
      const dataWithConfirm = {
        ...pending,
        ...(confirmedCapTypes.current.has('instructor') && pending.instructorAddedQuestions != null && {
          instructorAddedQuestions: { ...pending.instructorAddedQuestions, confirm: true }
        }),
        ...(confirmedCapTypes.current.has('section') && pending.sectionAddedQuestions != null && {
          sectionAddedQuestions: { ...pending.sectionAddedQuestions, confirm: true }
        })
      }
      const response = await editSlotMutation.mutateAsync({ slotId: selectedRow.slotId, data: dataWithConfirm })
      confirmedCapTypes.current.clear()
      const shouldClose = await afterEditSlotPatchSuccess(response)
      if (shouldClose) {
        onSaved?.()
        handleCloseModal()
      }
    } catch (err: any) {
      if (routeCapLoweringConflict(err, pendingData)) return
      confirmedCapTypes.current.clear()
      reportSaveError(err)
    } finally {
      setIsCreating(false)
    }
  }

  const onSubmit: SubmitHandler<FormType> = async (value) => {
    if (isTurningSectionTypeOff && !hasConfirmedSectionTypeOff.current) {
      setIsConfirmingSectionTypeOff(true)
      return
    }
    // A fresh Save is a fresh set of caps: acknowledgements from an attempt the admin cancelled, or
    // one that failed for an unrelated reason, must not carry into it.
    confirmedCapTypes.current.clear()

    const {
      templateId,
      termId,
      startTimestamp,
      endTimestamp,
      releaseTimestamp,
      isRollingResultsEnabled,
      isInstructorAddedQuestionsAllowed,
      isDefaultInstructorAddedQuestionsEnabled,
      instructorAddedQuestions,
      isAcademicLevelAddedQuestionsAllowed,
      academicLevelAddedQuestions,
      isSectionAddedQuestionsAllowed,
      sectionAddedQuestions,
      isSlotAnonymous,
      isAcademicUnitOptOutEnabled,
      academicUnitOptOutCategoryIds,
      academicUnitOptOutDeadline,
      isInstructorOptOutEnabled,
      instructorOptOutDeadline,
      isInstructorOptInOverrideEnabled
    } = value

    const isEditing = isEditAction
    const selectedTemplateRevision = (selectedRow as any)?.surveyTemplate?.templateRevision
    const templateRevisionToUpdate = isEditing
      ? Number(selectedTemplateRevision ?? 1)
      : 1
    const slotData = {
      termId,
      templateId,
      templateRevision: templateRevisionToUpdate,
      ...(canEditStartTimestamp && { startTimestamp }),
      ...(canEditEndTimestamp && { endTimestamp }),
      ...((!isRollingResultsEnabled && canEditReleaseTimestamp) && { releaseTimestamp }),
      isRollingResultsEnabled,
      isSlotAnonymous,
      isInstructorAddedQuestionsAllowed,
      instructorAddedQuestions,
      isAcademicLevelAddedQuestionsAllowed,
      // Steps 1 and 2 of the backend's three-step flow, in one PATCH: the flag turns the type on,
      // the block sets its limit. Order matters — `apply_default` now 422s unless both have landed
      // ("Please turn on Section Added Questions before enabling the default." / "Set a question
      // limit … before attaching a default template."), which is why the template call runs after
      // this PATCH rather than before it.
      isSectionAddedQuestionsAllowed,
      sectionAddedQuestions,
      isAcademicUnitOptOutEnabled,
      academicUnitOptOutCategoryIds,
      academicUnitOptOutDeadline,
      isInstructorOptOutEnabled,
      instructorOptOutDeadline,
      isInstructorOptInOverrideEnabled,
      academicLevelAddedQuestions: {
        ...academicLevelAddedQuestions,
        academicUnitCategories: academicLevelAddedQuestions?.academicUnitCategories.map(
          (academic: IAcademicUnit) => ({
            categoryId: academic.categoryId,
            categoryName: academic.categoryName
          })
        )
      }
    }

    const updatedVals = getDirtyValues(dirtyFields, slotData)
    // `getDirtyValues` keeps only dirty leaves, so a first enable where the admin left "Allow
    // creation of new custom questions" unticked would send `{ questionCount: N }` alone and let the
    // backend choose that flag. It is not cosmetic — false means instructors may only pick prompts
    // from the school's bank and cannot write their own — and the endpoint that used to hard-code it
    // `true` is exactly what the backend withdrew. So whenever this block travels at all, it travels
    // whole.
    //
    // It also has to travel whole when the *flag* is what changed, not just when the block is dirty.
    // Turning the type off PATCHes the flag alone and leaves the limit on the row, so re-ticking the
    // switch later dirties nothing else — the block would sit at its saved value, `getDirtyValues`
    // would drop it, and the body would be `{ isSectionAddedQuestionsAllowed: true }` on its own:
    // the flag-without-limit request the backend refuses.
    const isSectionFlagDirty = Boolean(dirtyFields.isSectionAddedQuestionsAllowed)
    if (isSectionAddedQuestionsAllowed !== true) {
      // The block is only writable while the flag is on, so it must not travel whenever the switch
      // is off — whether that is a turn-off (a limit edited and then abandoned in the same visit
      // would 422 about a type just switched off, or trip the cap-lowering 409 first and open a
      // dialog about a limit being removed rather than applied) or an off → on → off round trip,
      // which leaves the limit dirty while the flag goes back to its saved `false` and is therefore
      // never sent. Keyed on the switch's position, not on the transition: `isTurningSectionTypeOff`
      // compares against the slot and so does not see the round trip at all.
      delete updatedVals.sectionAddedQuestions
    } else if (updatedVals.sectionAddedQuestions != null || isSectionFlagDirty) {
      updatedVals.sectionAddedQuestions = {
        ...updatedVals.sectionAddedQuestions,
        questionCount: sectionAddedQuestions?.questionCount,
        isCustomQuestionAllowed: Boolean(sectionAddedQuestions?.isCustomQuestionAllowed)
      }
    }
    setIsCreating(true)
    try {
      if (isEditing) {
        let response
        try {
          response = await editSlotMutation.mutateAsync({ slotId: selectedRow?.slotId, data: updatedVals })
        } catch (err: any) {
          // Backend's cap-lowering conflict (lowering a questionCount below an already-touched
          // section's current count) — distinct from apply_default's own 409 (which carries
          // `summary`, not just `conflicts`). Anything else rethrows to the outer catch below.
          if (routeCapLoweringConflict(err, updatedVals)) return
          throw err
        }
        const shouldClose = await afterEditSlotPatchSuccess(response)
        if (!shouldClose) {
          // The modal stays open, so the admin can still change their mind about the turn-off —
          // which means the acknowledgement has to be asked for again rather than latching.
          hasConfirmedSectionTypeOff.current = false
          return
        }
      } else {
        const response = await createSlotMutation.mutateAsync({ projectId, data: slotData })
        const termName = response?.data?.term?.termName ?? ''
        notification('Term added successfully', { variant: 'success', subTitle: termName ? `Term: ${termName}` : undefined })
        // The two preconditions are new (2026-08-22): `apply_default` now refuses unless the type is
        // switched on *and* has a question limit, for instructor-added as well as section-added —
        // "Please turn on … before enabling the default." / "Set a question limit … before attaching
        // a default template." Creation carries both, so they hold whenever the admin actually
        // enabled the type; without checking them, a carried-over project whose instructor type is
        // off would fire this call and take a silent 422 straight to Sentry.
        if (
          response.data.slotId &&
          isInstructorAddedQuestionsAllowed === true &&
          Number(instructorAddedQuestions?.questionCount) > 0 &&
          isDefaultInstructorAddedQuestionsEnabled &&
          instructorAddedQuestions?.defaultTemplateId
        ) {
          // Copy Previous Project Settings carries the default-template setting into form state
          // for free, but the Backend's dump_only guard means slot creation itself silently
          // ignores it — this explicit call is what actually activates it. Fire-and-forget: the
          // new slot has zero sections/instructors yet, so no conflict can occur, and per spec
          // this carry-over is automatic and silent (log only, no user-facing error) either way.
          void AutoImportManager.applyDefaultInstructorAddedQuestions(response.data.slotId, {
            enabled: true,
            defaultSurveyTemplateId: instructorAddedQuestions.defaultTemplateId
          }).catch((error) => {
            // Spec: silent for the admin (no toast). Still report to Sentry so engineering
            // gets a signal when a new project silently fails to inherit defaults.
            console.error('Failed to carry over default instructor added questions:', error)
            Sentry.captureException(error)
          })
        }
        // CR-295 — the optional third step on a brand-new project. Creation itself carries the flag
        // and the limit (steps 1 and 2), so this is only the default template, and only when the
        // admin picked one. Awaited rather than fire-and-forget like the instructor carry-over
        // above: this is a choice made in this modal, not a setting inherited from a previous
        // project, so a failure has to be visible.
        //
        // A conflict is impossible here: the project has no sections yet, so there is nothing to
        // overwrite and `confirm` is never needed.
        if (
          response.data.slotId &&
          isSectionAddedQuestionsAllowed === true &&
          Number(sectionAddedQuestions?.questionCount) > 0 &&
          isDefaultSectionQuestionsEnabled &&
          selectedSectionDefaultTemplate
        ) {
          try {
            await AutoImportManager.applyDefaultSectionAddedQuestions(response.data.slotId, {
              enabled: true,
              defaultSurveyTemplateId: selectedSectionDefaultTemplate.templateId
            })
          } catch (error) {
            notifyError(error, { fallbackMessage: 'The survey project was created, but the default Course Section questions could not be applied.' })
            Sentry.captureException(error)
          }
        }
        if (response.data.slotId && !handleAddSlot) {
          navigate(`${ROUTES.COURSE_AND_ENROLLMENT}/${response.data.slotId}?projectId=${projectId}`)
        }
        handleAddSlot?.(response.data.slotId)
        await DistributionManager.getDistributionById(projectId)
      }
      onSaved?.()
      handleCloseModal()
    } catch (err: any) {
      // Same reasoning as the `!shouldClose` path above: a failed save leaves the modal open, so a
      // confirmed turn-off must not stay acknowledged into the next attempt.
      hasConfirmedSectionTypeOff.current = false
      reportSaveError(err)
    } finally {
      setIsCreating(false)
    }
  }
  const isAcademicLevelAddedQuestions = watch('isAcademicLevelAddedQuestionsAllowed') ?? false
  const isInstructorAddedQuestions = watch('isInstructorAddedQuestionsAllowed')
  const isRollingOn = watch('isRollingResultsEnabled')
  const isAcademicUnitsCustomQuestionAllowed = watch('academicLevelAddedQuestions.isCustomQuestionAllowed')
  const isInstructorCustomQuestionAllowed = watch('instructorAddedQuestions.isCustomQuestionAllowed')
  const isSectionAddedQuestions = watch('isSectionAddedQuestionsAllowed') ?? false
  // Still needed for the destructive turn-off confirmation, which is about the *slot* losing its
  // shared questions and so genuinely is a transition. The default-template calls used to gate on
  // this too and no longer do — they gate on the switch's position; there is no instructor-added
  // counterpart of this constant for that reason.
  const isTurningSectionTypeOff = isSectionTypeEnabledOnSlot && !isSectionAddedQuestions
  const isSectionCustomQuestionAllowed = watch('sectionAddedQuestions.isCustomQuestionAllowed')

  /**
   * Switching a type off clears its default-template selection.
   *
   * The picker and its checkbox live in `useState`, outside the form, so collapsing the block left
   * them selected — and `isTurning*TypeOff` compares the switch against the **slot**, so an
   * off → on → off round trip inside one modal is not recognised as a turn-off. Save then fired
   * `apply_default` with `enabled: true` against a type the PATCH had left off. Until the backend's
   * three-step change that call silently switched the type back on, which is why it went unnoticed;
   * it is a 422 now ("Please turn on … before enabling the default.").
   *
   * On the transition only, never on mount: a type that is *already* off can still legitimately
   * have a template attached — the backend keeps it so that state stays exitable — and clearing on
   * mount would make the next Save detach a template the admin never touched.
   */
  /**
   * What the clear-on-turn-off effects below took away, so turning the type back on can put it back.
   *
   * Clearing without restoring made a round trip destructive. Untick the type and the default's
   * enabled flag and template are cleared; re-tick it and nothing came back, because the effect only
   * fires on the way down. The form then looked untouched — `isDirty` false, the type back at its
   * original value — while `hasPendingSectionDefaultChange` still read `true` from
   * `initialSectionDefaultEnabledRef`, so Save posted `apply_default { enabled: false }` and
   * detached the project's default template, taking every section's untouched seeded questions with
   * it. No confirmation, because `isTurningSectionTypeOff` was false by then: the admin had put the
   * type back. Found by review.
   *
   * Restores what was cleared rather than what the modal opened with, so a template picked and then
   * toggled past survives the round trip too.
   */
  const clearedSectionDefaultRef = useRef<{
    enabled: boolean
    template: DefaultInstructorTemplateOption | null
  } | null>(null)
  const wasSectionTypeOn = useRef(isSectionAddedQuestions)
  React.useEffect(() => {
    if (wasSectionTypeOn.current && !isSectionAddedQuestions) {
      clearedSectionDefaultRef.current = {
        enabled: isDefaultSectionQuestionsEnabled,
        template: selectedSectionDefaultTemplate
      }
      setIsDefaultSectionQuestionsEnabled(false)
      setSelectedSectionDefaultTemplate(null)
      setSectionConflictData(null)
    } else if (!wasSectionTypeOn.current && isSectionAddedQuestions && clearedSectionDefaultRef.current !== null) {
      setIsDefaultSectionQuestionsEnabled(clearedSectionDefaultRef.current.enabled)
      setSelectedSectionDefaultTemplate(clearedSectionDefaultRef.current.template)
      clearedSectionDefaultRef.current = null
    }
    wasSectionTypeOn.current = isSectionAddedQuestions
    // `isDefaultSectionQuestionsEnabled` and `selectedSectionDefaultTemplate` are read, not
    // tracked: this must run when the *type* toggles, and adding them would re-run it on every
    // template pick, clearing the pick it had just recorded.
  }, [isSectionAddedQuestions])

  /** The instructor type's identical round trip, with the identical consequence on Save. */
  const clearedInstructorDefaultRef = useRef<{
    enabled: boolean
    template: DefaultInstructorTemplateOption | null
  } | null>(null)
  const wasInstructorTypeOn = useRef(Boolean(isInstructorAddedQuestions))
  React.useEffect(() => {
    if (wasInstructorTypeOn.current && !isInstructorAddedQuestions) {
      clearedInstructorDefaultRef.current = {
        enabled: isDefaultInstructorQuestionsEnabled,
        template: selectedDefaultTemplate
      }
      setIsDefaultInstructorQuestionsEnabled(false)
      setSelectedDefaultTemplate(null)
      setDefaultQuestionsConflictData(null)
    } else if (!wasInstructorTypeOn.current && Boolean(isInstructorAddedQuestions) && clearedInstructorDefaultRef.current !== null) {
      setIsDefaultInstructorQuestionsEnabled(clearedInstructorDefaultRef.current.enabled)
      setSelectedDefaultTemplate(clearedInstructorDefaultRef.current.template)
      clearedInstructorDefaultRef.current = null
    }
    wasInstructorTypeOn.current = Boolean(isInstructorAddedQuestions)
    // Read-not-tracked, as above.
  }, [isInstructorAddedQuestions])

  const isAcademicUnitOptOutEnabled = watch('isAcademicUnitOptOutEnabled') ?? false
  const isInstructorOptOutEnabled = watch('isInstructorOptOutEnabled') ?? false
  const isInstructorOptInOverrideEnabled = watch('isInstructorOptInOverrideEnabled') ?? true

  const hasEnabledAdvancedSettings = Boolean(
    isAcademicUnitOptOutEnabled ||
    isInstructorOptOutEnabled ||
    isAcademicLevelAddedQuestions ||
    isInstructorAddedQuestions ||
    // The Collapse below is unmountOnExit, so a slot whose only enabled advanced setting is
    // section-added questions would open with the block not even rendered.
    isSectionAddedQuestions
  )
  const [showAdvanced, setShowAdvanced] = useState(hasEnabledAdvancedSettings)

  const handleToggleNext = (): void => {
    if (hasEnabledAdvancedSettings) {
      setShowAdvanced(true)
    }
    setStep(STEP.FORM)
  }

  const surveyTemplate = watch('surveyTemplate')

  return (
    <>
      {step === STEP.SELECTDEFAULT && !isEditAction && (
        <FormProvider {...methods}>
          <DefaultSurveySelection
            methods={methods}
            distributionSeriesId={projectId}
            shouldLoadLatestDefaults={shouldLoadLatestDefaults}
            setStep={setStep}
          />
        </FormProvider>
      )}
      {step === STEP.TOGGLE && !isEditAction && (
        <FormProvider {...methods}>
          <SurveyProjectTypeAndRollingBasisToggle
            handleCloseModal={handleCloseModal}
            loading={loading}
            isCreating={isCreating}
            isSlotStatusLive={isSlotStatusLive}
            onNext={handleToggleNext}
          />
        </FormProvider>
      )}
      {(step === STEP.FORM || isEditAction) && (
        <FormProvider {...methods}>
          <ValidationAlert errors={errors} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" flexDirection="column" gap={theme.spacing(6)}>
              <ApiAutoComplete
                apiEndpoint="/api/terms/summary/"
                labelField="termName"
                label="Term"
                name="termId"
                required
                selectedItem={selectedRow?.term}
                readOnly={!canEditTerm}
                placeholder="Select term"
                apiParams={{
                  slotId: selectedRow?.slotId,
                  ...(!isEditAction && {
                    distributionSeriesId: projectId,
                    isPresent: false
                  })
                }}
              />
              <ApiAutoComplete
                apiEndpoint="/api/templates/summary/"
                label="Survey Template"
                labelField="title"
                name="templateId"
                required
                selectedItem={surveyTemplate}
                readOnly={!canEditSurveyTemplate}
                placeholder="Select Survey Template"
              />
              <GenericDateTimePicker
                name="startTimestamp"
                label="Start Date"
                required
                minDate={canEditStartTimestamp ? todayMin : undefined}
                disabled={!canEditStartTimestamp}
                placeholder="Enter Start Date"
                onChange={() => {
                  lastChangedField.current = 'start'
                  void trigger(['endTimestamp', 'releaseTimestamp'])
                }}
              />
              <Typography color={grey[850]} fontSize="14px" maxWidth={'90%'} mt={-4}>
                First day a survey can go live. All course section survey start dates must be on or after this date.
              </Typography>
              <GenericDateTimePicker
                name="endTimestamp"
                label="End Date"
                required
                minDate={canEditEndTimestamp ? todayMin : undefined}
                disabled={!canEditEndTimestamp}
                placeholder="Enter End Date"
                onChange={() => {
                  lastChangedField.current = 'end'
                  void trigger(['startTimestamp', 'releaseTimestamp'])
                }}
              />
              <Typography color={grey[850]} fontSize="14px" maxWidth={'90%'} mt={-4}>
                Last day surveys can remain live. All course section survey end dates must be on or before this date.
              </Typography>
              {(!isRollingOn && selectedRow?.releaseTimestamp !== ReleaseMode.Rolling) && (
                <>
                  <GenericDateTimePicker
                    name="releaseTimestamp"
                    label="Results Release Date"
                    required
                    minDate={canEditReleaseTimestamp ? todayMin : undefined}
                    disabled={!canEditReleaseTimestamp}
                    placeholder="Enter Release Date"
                    onChange={() => {
                      lastChangedField.current = 'release'
                      void trigger(['endTimestamp'])
                    }}
                  />
                  <Typography color={grey[850]} fontSize="14px" maxWidth={'90%'} mt={-4}>
                    Final day results can remain unreleased. Aggregate results will be released on this date.
                  </Typography>
                  {/* Closed projects only, because that is the only status where the backend clamps
                      section early-access dates to this one. Warned before the save rather than
                      reported after it: the clamp cannot be reversed from the UI, so an admin who
                      moves this date earlier by mistake and corrects it will find the section dates
                      left at the intermediate value. */}
                  {isSlotInClosed && (
                    <Typography color={grey[850]} fontSize="14px" fontWeight={500} maxWidth={'90%'} mt={-2}>
                      Moving this date earlier will move any course section Admin or Instructor Release Date that falls after it back to this date. That cannot be undone here — the previous dates are kept on the Release Date Sync task record, which the Activity Monitor lists.
                    </Typography>
                  )}
                </>
              )}
              <Divider />
              <Box
                component="button"
                type="button"
                onClick={() => { setShowAdvanced((prev) => !prev) }}
                aria-expanded={showAdvanced}
                aria-controls="advanced-settings-content"
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 40,
                  py: 1,
                  px: 0,
                  border: 0,
                  backgroundColor: 'transparent',
                  color: 'inherit',
                  cursor: 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: theme.spacing(1),
                    borderRadius: theme.spacing(1)
                  }
                }}
              >
                <Typography color={grey[800]} fontWeight={600}>Advanced Settings</Typography>
                <ExpandLessIcon
                  sx={{
                    color: grey[250],
                    transform: showAdvanced ? 'rotate(0deg)' : 'rotate(180deg)',
                    transition: theme.transitions.create('transform', {
                      duration: theme.transitions.duration.shortest
                    })
                  }}
                />
              </Box>
              {!canEditAdvancedSettings && !hasEnabledAdvancedSettings && (
                <Typography role="status" fontSize="13px" color={grey[650]}>
                  Advanced settings can only be configured while the project is in Draft status.
                </Typography>
              )}
              <Collapse id="advanced-settings-content" in={showAdvanced} timeout="auto" unmountOnExit sx={{ mb: 4 }}>
                {/* OPT OUT PERMISSION */}
                <Box sx={{ ml: 2 }}>
                  <Typography mb={2} color={grey[850]} fontSize="18px" fontWeight={500}>
                    Opt out permissions
                  </Typography>
                  <FormSwitch
                    name="isAcademicUnitOptOutEnabled"
                    label="Allow Academic Units to Opt out"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isAcademicUnitOptOutEnabled || false}
                  />
                  <AnimatedContainer isVisible={isAcademicUnitOptOutEnabled}>
                    <Box>
                      <MultiSelectApiAutoComplete
                        apiEndpoint={`/api/acad_units/depth/${academicUnitDepth}/children/summary/`}
                        label="Academic Unit level(s):"
                        placeholder="Select Academic Unit Levels"
                        name="academicUnitOptOutCategoryIds"
                        getOptionLabel={(c: IAcademicUnit) => c.categoryName}
                        getOptionValue={(c: IAcademicUnit) => c.categoryId}
                        getOptionDisabled={(c: IAcademicUnit) => c.categoryName === 'University'}
                        disabled={isSlotStatusLive}
                      />
                      <Typography my={2} fontSize="16px" color={grey[850]}>
                        Admins at the selected Academic Unit levels will be able to opt out their academic units and sections.
                      </Typography>
                      <Typography mb={1} mt={1} fontSize="16px" color={grey[850]} fontWeight={500}>
                        Academic Unit Opt-out Deadline:
                      </Typography>
                      <Box display={'flex'} gap={2} >
                        <FormInput
                          control={control}
                          name="academicUnitOptOutDeadline"
                          placeholder="Days"
                          type="number"
                          disabled={isSlotStatusLive}
                          wrapperStyle={{ width: '120px' }}
                        />
                        <Typography fontSize="16px" color={grey[850]} mt={2}>
                          days before Survey Project/Course Section Start Date
                        </Typography>
                      </Box>
                    </Box>
                  </AnimatedContainer>
                  {isAcademicLevelAddedQuestions && <Divider sx={{ my: 4 }} />}
                  <FormSwitch
                    name="isInstructorOptOutEnabled"
                    label="Allow Instructors to Opt out"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isInstructorOptOutEnabled || false}
                  />
                  <AnimatedContainer isVisible={isInstructorOptOutEnabled} >
                    <Typography my={1} fontSize="16px" color={grey[850]} fontWeight={500}>
                      Course Section Opt-out Deadline:
                    </Typography>
                    <Box display={'flex'} gap={2} mb={2}>
                      <FormInput
                        control={control}
                        name="instructorOptOutDeadline"
                        placeholder="Days"
                        type="number"
                        disabled={isSlotStatusLive}
                        wrapperStyle={{ width: '120px', minHeight: 'unset' }}
                      />
                      <Typography fontSize="16px" color={grey[850]} mt={2}>
                        days before Course Section Start Date
                      </Typography>
                    </Box>
                    <FormCheckbox
                      control={control}
                      name="isInstructorOptInOverrideEnabled"
                      label="Allow Instructor Opt in Override"
                      sx={{ ml: -3, fontSize: '16px' }}
                      disabled={isSlotStatusLive}
                      onChange={(e) => {
                        methods.setValue(
                          'isInstructorOptInOverrideEnabled',
                          e.target.checked,
                          { shouldDirty: true }
                        )
                      }}
                      checked={isInstructorOptInOverrideEnabled}
                    />
                  </AnimatedContainer>
                </Box>
                <Divider />
                {/* CUSTOM QUESTIONS */}
                <Box sx={{ ml: 2, mt: 4 }}>
                  <Typography mb={2} color={grey[850]} fontSize="18px" fontWeight={500}>
                    Custom Questions
                  </Typography>
                  <FormSwitch
                    name="isAcademicLevelAddedQuestionsAllowed"
                    label="Allow Academic Units to add questions"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isAcademicLevelAddedQuestions || false}
                  />
                  <AnimatedContainer isVisible={isAcademicLevelAddedQuestions}>
                    <Box>
                      <MultiSelectApiAutoComplete
                        apiEndpoint={`/api/acad_units/depth/${academicUnitDepth}/children/summary/`}
                        label="Academic Unit level(s)"
                        placeholder="Select Academic Unit(s)"
                        name="academicLevelAddedQuestions.academicUnitCategories"
                        getOptionLabel={(c: IAcademicUnit) => c.categoryName}
                        getOptionValue={(c: IAcademicUnit) => c.categoryId}
                        getOptionDisabled={(c: IAcademicUnit) => c.categoryName === 'University'}
                        disabled={isSlotStatusLive}
                      />
                      <Typography maxWidth={420} mb={2} fontSize="16px" color={grey[850]}>
                        Admins at the selected Academic Unit levels will be able to add questions on this survey
                      </Typography>
                      <FormInput
                        control={control}
                        name="academicLevelAddedQuestions.questionCount"
                        placeholder="Number of questions"
                        type="number"
                        disabled={isSlotStatusLive}
                        label="Number of questions"
                      />
                      <Typography mb={1} mt={1} fontSize="16px" color={grey[850]}>
                        Maximum number of questions an admin can add.
                      </Typography>
                      <FormCheckbox
                        control={control}
                        name="academicLevelAddedQuestions.isCustomQuestionAllowed"
                        label="Allow creation of new custom questions"
                        sx={{ ml: -3, fontSize: '16px' }}
                        disabled={isSlotStatusLive}
                        onChange={(e) => {
                          methods.setValue(
                            'academicLevelAddedQuestions.isCustomQuestionAllowed',
                            e.target.checked,
                            { shouldDirty: true }
                          )
                        }}
                        checked={isAcademicUnitsCustomQuestionAllowed || false}
                      />
                      <Typography mt={-1} ml={8} mb={1} fontSize="16px" color={grey[850]}>
                        Admin will be able to create their own questions.
                      </Typography>
                    </Box>
                  </AnimatedContainer>
                  {isAcademicLevelAddedQuestions && <Divider sx={{ my: 4 }} />}
                  {/*
                    CR-295 — section-added questions. Placed between the Academic Unit and
                    Instructor blocks so this section reads widest scope to narrowest: unit,
                    then section, then instructor. The two instructor-facing rows are
                    deliberately adjacent, each stating how its questions are asked, because
                    they are the pair an admin can confuse.
                  */}
                  <FormSwitch
                    name="isSectionAddedQuestionsAllowed"
                    label="Allow Course Section questions"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isSectionAddedQuestions || false}
                  />
                  <AnimatedContainer isVisible={isSectionAddedQuestions}>
                    <Box>
                      <Typography maxWidth={420} mb={2} fontSize="16px" color={grey[850]}>
                        One set per Course Section, asked once, and any instructor on the section can edit it.
                      </Typography>
                      <FormInput
                        control={control}
                        name="sectionAddedQuestions.questionCount"
                        placeholder="Number of questions per Course Section"
                        type="number"
                        disabled={isSlotStatusLive}
                        label="Number of questions per Course Section"
                      />
                      <Typography color={grey[850]} mt={1} fontSize="16px">
                        Maximum questions that can be added for the whole Course Section.
                      </Typography>
                      <FormCheckbox
                        control={control}
                        name="sectionAddedQuestions.isCustomQuestionAllowed"
                        label="Allow creation of new custom questions"
                        sx={{ ml: -3, fontSize: '16px' }}
                        disabled={isSlotStatusLive}
                        onChange={(e) => {
                          methods.setValue(
                            'sectionAddedQuestions.isCustomQuestionAllowed',
                            e.target.checked,
                            { shouldDirty: true }
                          )
                        }}
                        checked={isSectionCustomQuestionAllowed || false}
                      />
                      <Typography ml={8} mt={-1} fontSize="16px" color={grey[850]}>
                        Instructors will be able to create their own questions for the section.
                      </Typography>
                      <FormCheckbox
                        control={sectionDefaultTemplateControl}
                        name="isSectionDefaultEnabled"
                        label="Use default Course Section questions"
                        sx={{ ml: -3, fontSize: '16px' }}
                        disabled={isSlotStatusLive || isDefaultQuestionsBusy}
                        onChange={(e) => { handleToggleDefaultSectionQuestions(e.target.checked) }}
                        checked={isDefaultSectionQuestionsEnabled}
                      />
                      {!selectedRow?.slotId && (
                        <Typography ml={8} mt={-1} mb={1} fontSize="13px" color={grey[650]}>
                          The template is applied as soon as the survey project is created.
                        </Typography>
                      )}
                      <AnimatedContainer isVisible={isDefaultSectionQuestionsEnabled}>
                        <Box sx={{ ml: 8 }}>
                          <ApiAutoComplete
                            apiEndpoint="/api/templates/summary/"
                            label="Default Question Template"
                            labelField="title"
                            name="sectionTemplateId"
                            // ApiAutoComplete defaults `valueField` to `name`
                            // (ApiAutoComplete/index.tsx:46). The form field is named
                            // sectionTemplateId to stay distinct from the instructor picker's,
                            // but the objects from /api/templates/summary/ key on `templateId` —
                            // so without this the component looks for `sectionTemplateId` on
                            // every option, finds nothing, and no selection can ever match.
                            valueField="templateId"
                            control={sectionDefaultTemplateControl}
                            selectedItem={selectedSectionDefaultTemplate ?? undefined}
                            onChange={(value: DefaultInstructorTemplateOption | null) => { setSelectedSectionDefaultTemplate(value); clearSectionDefaultTemplateErrors('sectionTemplateId') }}
                            getOptionLabel={(t: DefaultInstructorTemplateOption) =>
                              t.questionCount != null ? `${t.title} (${t.questionCount} questions)` : t.title}
                            placeholder="Select a template"
                            readOnly={isSlotStatusLive || isDefaultQuestionsBusy}
                          />
                        </Box>
                      </AnimatedContainer>
                    </Box>
                  </AnimatedContainer>
                  {isSectionAddedQuestions && <Divider sx={{ my: 4 }} />}
                  <FormSwitch
                    name="isInstructorAddedQuestionsAllowed"
                    label="Allow Instructor to add questions"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isInstructorAddedQuestions || false}
                  />
                  <AnimatedContainer isVisible={isInstructorAddedQuestions}>
                    <Box>
                      <Typography maxWidth={420} mb={2} fontSize="16px" color={grey[850]}>
                        Asked separately for each instructor on the section.
                      </Typography>
                      <FormInput
                        control={control}
                        name="instructorAddedQuestions.questionCount"
                        placeholder="Number of questions per Instructor"
                        type="number"
                        disabled={isSlotStatusLive}
                        label="Number of questions per Instructor"
                      />
                      <Typography color={grey[850]} mt={1} fontSize="16px">
                        Maximum questions instructors can add.
                      </Typography>
                      <FormCheckbox
                        control={control}
                        name="instructorAddedQuestions.isCustomQuestionAllowed"
                        label="Allow creation of new custom questions"
                        sx={{ ml: -3, fontSize: '16px' }}
                        disabled={isSlotStatusLive}
                        onChange={(e) => {
                          methods.setValue(
                            'instructorAddedQuestions.isCustomQuestionAllowed',
                            e.target.checked,
                            { shouldDirty: true }
                          )
                        }}
                        checked={isInstructorCustomQuestionAllowed || false}
                      />
                      <Typography ml={8} mt={-1} fontSize="16px" color={grey[850]}>
                        Instructors will be able to create their own questions.
                      </Typography>
                      <FormCheckbox
                        control={defaultTemplateControl}
                        name="isDefaultEnabled"
                        label="Use default instructor added questions"
                        sx={{ ml: -3, fontSize: '16px' }}
                        disabled={isSlotStatusLive || !selectedRow?.slotId || isDefaultQuestionsBusy}
                        onChange={(e) => { handleToggleDefaultInstructorQuestions(e.target.checked) }}
                        checked={isDefaultInstructorQuestionsEnabled}
                      />
                      {!selectedRow?.slotId && (
                        <Typography ml={8} mt={-1} mb={1} fontSize="13px" color={grey[650]}>
                          Available once the survey project has been created.
                        </Typography>
                      )}
                      <AnimatedContainer isVisible={isDefaultInstructorQuestionsEnabled}>
                        <Box sx={{ ml: 8 }}>
                          <ApiAutoComplete
                            apiEndpoint="/api/templates/summary/"
                            label="Default Question Template"
                            labelField="title"
                            name="templateId"
                            control={defaultTemplateControl}
                            selectedItem={selectedDefaultTemplate ?? undefined}
                            onChange={(value: DefaultInstructorTemplateOption | null) => { setSelectedDefaultTemplate(value); clearDefaultTemplateErrors('templateId') }}
                            getOptionLabel={(t: DefaultInstructorTemplateOption) =>
                              t.questionCount != null ? `${t.title} (${t.questionCount} questions)` : t.title}
                            placeholder="Select a template"
                            readOnly={isSlotStatusLive || isDefaultQuestionsBusy}
                          />
                        </Box>
                      </AnimatedContainer>
                    </Box>
                  </AnimatedContainer>
                </Box>
              </Collapse>

              {isConfirmingSectionTypeOff && (
                <GenericModal
                  isOpen
                  onClose={() => { setIsConfirmingSectionTypeOff(false) }}
                  title="Turn off Course Section questions?"
                  maxWidth="sm"
                  action={false}
                >
                  <Typography fontSize="14px" mb={2}>
                    This removes shared questions from every course section in this survey project.
                    Questions an instructor edited or wrote are kept; anything still untouched from
                    the template is removed.
                  </Typography>
                  <DefaultModalActions
                    destructiveButtonProps={{
                      onClick: () => { setIsConfirmingSectionTypeOff(false) },
                      disabled: isCreating
                    }}
                    confirmButtonProps={{
                      onClick: () => {
                        hasConfirmedSectionTypeOff.current = true
                        setIsConfirmingSectionTypeOff(false)
                        void handleSubmit(onSubmit)()
                      },
                      text: 'Turn Off',
                      loading: isCreating
                    }}
                  />
                </GenericModal>
              )}

              {/* CR-295 — the section-added counterpart. Its own modal because the 409 body has
                  no per-instructor dimension; see ApplySectionDefaultQuestionsConflictModal. */}
              {sectionConflictData && (
                <GenericModal
                  isOpen
                  onClose={() => { setSectionConflictData(null) }}
                  title="Update Existing Course Section Questions?"
                  maxWidth="sm"
                  action={false}
                >
                  <ApplySectionDefaultQuestionsConflictModal
                    conflicts={sectionConflictData.conflicts}
                    summary={sectionConflictData.summary}
                    isSubmitting={isDefaultQuestionsBusy}
                    onCancel={() => { setSectionConflictData(null) }}
                    onConfirm={() => {
                      void (async () => {
                        const outcome = await applyDefaultSectionQuestions(true)
                        if (outcome === 'applied') {
                          onSaved?.()
                          handleCloseModal()
                        }
                      })()
                    }}
                    onForceConfirm={() => {
                      void (async () => {
                        const outcome = await applyDefaultSectionQuestions(true, { force: true })
                        if (outcome === 'applied') {
                          onSaved?.()
                          handleCloseModal()
                        }
                      })()
                    }}
                  />
                </GenericModal>
              )}

              {defaultQuestionsConflictData && (
                <GenericModal
                  isOpen
                  onClose={() => { setDefaultQuestionsConflictData(null) }}
                  title="Update Existing Instructor Questions?"
                  maxWidth="sm"
                  action={false}
                >
                  <ApplyDefaultQuestionsConflictModal
                    conflicts={defaultQuestionsConflictData.conflicts}
                    summary={defaultQuestionsConflictData.summary}
                    isSubmitting={isDefaultQuestionsBusy}
                    onCancel={() => { setDefaultQuestionsConflictData(null) }}
                    onConfirm={() => {
                      void (async () => {
                        // Save already committed the rest of the form before this conflict
                        // surfaced (see onSubmit) — finish the deferred close only once this
                        // last piece resolves too.
                        const outcome = await applyDefaultInstructorQuestions(true)
                        if (outcome === 'applied') {
                          onSaved?.()
                          handleCloseModal()
                        }
                      })()
                    }}
                    onForceConfirm={() => {
                      void (async () => {
                        const outcome = await applyDefaultInstructorQuestions(true, { force: true })
                        if (outcome === 'applied') {
                          onSaved?.()
                          handleCloseModal()
                        }
                      })()
                    }}
                  />
                </GenericModal>
              )}

              {sectionCapLoweringConflict && (
                <GenericModal
                  isOpen
                  onClose={() => { setSectionCapLoweringConflict(null) }}
                  title="Some sections are over the new limit"
                  maxWidth="sm"
                  action={false}
                >
                  <SectionCapLoweringConflictModal
                    conflicts={sectionCapLoweringConflict.conflicts}
                    newQuestionCount={sectionCapLoweringConflict.newQuestionCount}
                    isSubmitting={isCreating}
                    onCancel={() => { setSectionCapLoweringConflict(null) }}
                    onConfirm={() => { void confirmCapLoweringConflict(sectionCapLoweringConflict.pendingData, 'section') }}
                  />
                </GenericModal>
              )}

              {capLoweringConflict && (
                <GenericModal
                  isOpen
                  onClose={() => { setCapLoweringConflict(null) }}
                  title="Lower Instructor Question Cap?"
                  maxWidth="sm"
                  action={false}
                >
                  <CapLoweringConflictModal
                    conflicts={capLoweringConflict.conflicts}
                    newQuestionCount={capLoweringConflict.newQuestionCount}
                    isSubmitting={isCreating}
                    onCancel={() => { setCapLoweringConflict(null) }}
                    onConfirm={() => { void confirmCapLoweringConflict(capLoweringConflict.pendingData, 'instructor') }}
                  />
                </GenericModal>
              )}

            </Box>
            <DefaultModalActions
              destructiveButtonProps={
                isEditAction
                  ? { onClick: handleCloseModal, disabled: loading || isCreating }
                  : {
                      text: 'Back',
                      onClick: () => { setStep(STEP.TOGGLE) },
                      disabled: loading || isCreating
                    }
              }
              confirmButtonProps={{
                text: isEditAction ? 'Save' : 'Create',
                loading: loading || isCreating,
                type: 'submit',
                // A default-template change lives in its own isolated form (see
                // defaultTemplateControl above) specifically so it never dirties the main
                // form — but Save is now what actually submits it, so it must still enable
                // the button on its own. Both types count: the section-added picker has its
                // own isolated control too, so leaving it out would leave a section-only
                // default change unsavable.
                disabled: isEditAction && !isDirty && !hasPendingDefaultTemplateChange && !hasPendingSectionDefaultChange
              }}
              lastUpdateMeta={isEditAction && (
                <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
              )}
            />
          </form>
        </FormProvider>
      )}
    </>
  )
}
